import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles, Mail, ExternalLink } from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { api, COLORS } from "@/lib/api";

const MailLink = ({ email }) => (
  <a
    href={`mailto:${email}`}
    className="font-semibold text-brand-magenta hover:underline break-all"
  >
    {email}
  </a>
);

const Ext = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1 font-semibold text-brand-magenta hover:underline break-all"
  >
    {children} <ExternalLink size={13} className="flex-shrink-0" />
  </a>
);

const FAQS = [
  {
    q: "¿Cómo opera el modelo de Juventudes Latentes?",
    a: (
      <p>
        Somos una asociación civil sin fines de lucro afiliada a Juventudes Latentes
        (nacida en la Fundación de las Naciones Unidas y con sede en el Fondo Global por
        la Niñez). Operamos mediante un modelo de acompañamiento a clubs juveniles con
        metodología de liderazgo tripartita (propio, de pares y comunitario). Nos
        financiamos a través de alianzas estratégicas, convocatorias de fundaciones
        nacionales e internacionales, contratos de servicios con gobiernos y sector
        privado, y donaciones institucionales.
      </p>
    ),
  },
  {
    q: "¿Dónde se ubican? ¿Trabajan sólo en México?",
    a: (
      <>
        <p>
          Trabajamos de manera virtual a nivel nacional en 17 estados de la república
          mexicana y, de manera presencial, tenemos nuestra oficina en la Ciudad de México
          donde se encuentra nuestro cuerpo directivo y un espacio de uso común para los
          Clubs de Juventudes Latentes. ¡Te esperamos!
        </p>
        <p>
          Juventudes Latentes trabaja a nivel internacional operando en todos los
          continentes del mundo. Como afiliado, en México y algunos países de Centroamérica
          contamos con Clubs de más de una nacionalidad.
        </p>
      </>
    ),
  },
  {
    q: "¿Con quiénes trabaja Juventudes Latentes?",
    a: (
      <p>
        Trabajamos con adolescentes y jóvenes mujeres a través de 80 clubs activos en 23
        estados de México, en alianza con instituciones como UNICEF México, SEMUJERES,
        CONALEP, instituciones educativas, gobiernos locales, fundaciones internacionales y
        organizaciones de la sociedad civil comprometidas con la igualdad de género y el
        desarrollo juvenil.
      </p>
    ),
  },
  {
    q: "¿Qué tipo de proyectos realizamos con gobiernos federales y locales?",
    a: (
      <p>
        Con gobiernos locales, implementamos programas de desarrollo de liderazgo juvenil
        en instituciones educativas públicas como CONALEP Nuevo León (proyecto de 150
        estudiantes en 5 planteles). Desarrollamos escuelas de construcción de ciudadanía
        juvenil, programas de prevención de violencia de género y embarazo temprano, y
        apoyamos en la creación de mecanismos de participación ciudadana para adolescentes
        a nivel estatal y municipal.
      </p>
    ),
  },
  {
    q: "¿Qué tipo de proyectos realizamos con la sociedad civil organizada?",
    a: (
      <p>
        Hemos desarrollado manuales de prevención de embarazo temprano y violencia de
        género con UNICEF México, implementado programas STEM con perspectiva de género
        (STEM Bootcamp 2022, WiSci Camp México 2023 con 100 adolescentes de 5 países
        latinoamericanos), y colaboramos con organizaciones como Fondo Semillas, Girls Not
        Brides, Balance A.C. y Borde Político en programas de capacitación en liderazgo,
        derechos sexuales y reproductivos, y educación política juvenil.
      </p>
    ),
  },
  {
    q: "¿Qué tipo de proyectos realizamos con la industria?",
    a: (
      <p>
        Con el sector privado, hemos recibido financiamiento de fundaciones corporativas
        como Ford, Coppel, Moleskine y Stanley 1913 para programas de desarrollo de
        liderazgo juvenil. Desarrollamos talleres especializados (como desarrollo de pitch
        para programas STEM gubernamentales) y programas de reconocimiento a clubs (Top
        Clubs). Además, gestionamos alianzas para distribución de materiales educativos y
        recursos que fortalecen nuestros programas de acompañamiento a nivel nacional.
      </p>
    ),
  },
  {
    q: "¿Cómo se financia Juventudes Latentes?",
    a: (
      <p>
        Juventudes Latentes se financia a través de alianzas estratégicas con organismos
        internacionales, fundaciones filantrópicas nacionales, donantes individuales y
        empresas. Entre 2023-2025, hemos contado con el apoyo de organizaciones como UN
        Foundation, Ford Foundation, UNICEF (como colaborador) y otras instituciones
        comprometidas con nuestros objetivos de fortalecimiento de liderazgos adolescentes
        y advocacy, como BALANCE A.C., Fondo Semillas y Fundación Diez Morodo. En cuanto a
        empresas, hemos contado con el apoyo de Bonafont, MercadoLibre, entre otras.
      </p>
    ),
  },
  {
    q: "¿De qué maneras puedo hacer un donativo a Juventudes Latentes?",
    a: (
      <p>
        Juventudes Latentes cuenta con autorización de donataria autorizada ante el SAT, lo
        que significa que los donativos son deducibles de impuestos en México. Para realizar
        una aportación, las personas interesadas pueden escribir a{" "}
        <MailLink email="comunidad@latentes.mx" /> para recibir información sobre cómo
        proceder. La organización ha realizado campañas a través de PayPal y también recauda
        fondos mediante la plataforma Benevity.
      </p>
    ),
  },
  {
    q: "¿Dan pláticas o talleres? ¿Tienen prácticas y pasantías?",
    a: (
      <>
        <p>
          ¡Claro! Es una parte esencial de nuestro trabajo para impulsar narrativas de
          juventudes en todos los espacios. Escríbenos un correo a{" "}
          <MailLink email="luisa@latentes.mx" /> para explorar esta posibilidad.
        </p>
        <p>
          <span className="font-semibold text-brand-ink">
            ¿Tienen oportunidades para prácticas y pasantías?
          </span>{" "}
          Sí. Escríbenos un correo a <MailLink email="comunidad@latentes.mx" /> para
          explorar esta posibilidad.
        </p>
      </>
    ),
  },
  {
    q: "¿Cómo puedo unirme?",
    a: (
      <>
        <p>Hay tres formas de hacerlo:</p>
        <ol className="list-decimal pl-5 space-y-2">
          <li>Unirte a un Club existente en tu estado con base en tu edad.</li>
          <li>
            Abrir tu propio Club siguiendo los pasos de la página{" "}
            <Link
              to="/red-de-clubs/como-abrir-un-club"
              className="font-semibold text-brand-magenta hover:underline"
            >
              Abrir mi Club
            </Link>
            .
          </li>
          <li>
            Regístrate como voluntaria de programas en Juventudes Latentes para formar
            parte activa del cuerpo organizacional.
          </li>
        </ol>
      </>
    ),
  },
  {
    q: "¿Qué beneficios existen al estar en un Club?",
    a: (
      <>
        <p>
          Los Clubs de Juventudes Latentes son liderados por niñas, poniéndolas al frente de
          las acciones dentro de sus comunidades. Están conectadas a una red global de otras
          líderes; se conocen en eventos, campamentos y en línea, creando una comunidad
          global sin importar geografía o cultura.
        </p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>
            <span className="font-semibold text-brand-ink">Participación en foros:</span>{" "}
            pueden convertirse en voceras de la organización y representarnos en paneles,
            foros y conferencias.
          </li>
          <li>
            <span className="font-semibold text-brand-ink">Eventos internacionales:</span>{" "}
            acompañamos a la comunidad para participar en eventos como la COP, LCOY, ECOSOC
            Youth Forum, etc.
          </li>
          <li>
            <span className="font-semibold text-brand-ink">Espacio en medios:</span>{" "}
            alentamos a escribir columnas de opinión para medios de comunicación aliados.
          </li>
          <li>
            <span className="font-semibold text-brand-ink">
              Servicio social y prácticas profesionales
            </span>{" "}
            validables con nuestra organización.
          </li>
          <li>Comunidad global de agentes de cambio.</li>
          <li>Financiamiento para proyectos del Club.</li>
          <li>Incidencia local, nacional, regional e internacional.</li>
          <li>Capacitación constante y acceso a recursos de conocimiento.</li>
        </ul>
      </>
    ),
  },
  {
    q: "¿Qué hace único a Juventudes Latentes? ¿Quién puede formar parte de un Club?",
    a: (
      <>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>
            Deja que las niñas, listas para actuar dentro de sus comunidades, tomen el
            control como líderes y miembros de Clubs.
          </li>
          <li>
            Ayuda a que las niñas saquen su poder interior para trabajar en sus habilidades,
            derechos y oportunidades para liderar.
          </li>
          <li>
            Está comprometido a escuchar y amplificar las voces de las agentes de cambio,
            elevando la discusión global sobre la igualdad de género.
          </li>
        </ul>
        <p>
          <span className="font-semibold text-brand-ink">
            ¿Quién puede formar parte de un Club?
          </span>{" "}
          Cualquier niña y adolescente entre 13 y 24 años que resida en alguna de las 32
          entidades de México. No importa su lugar de proveniencia o identidad. Reconocemos
          todas las identidades sexuales y generamos espacios seguros para la comunidad
          LGBTQ+ adolescente.
        </p>
      </>
    ),
  },
  {
    q: "¿Tiene algún costo estar en Juventudes Latentes?",
    a: (
      <p>
        Recaudar fondos no es obligatorio; no cuesta nada unirse a Juventudes Latentes. De
        hecho, las chicas pueden solicitar apoyos financieros en los periodos de becas que
        las ayuden a actuar dentro de sus comunidades. Los fondos que recolectan los Clubs
        son exclusivamente para financiar sus propios proyectos.
      </p>
    ),
  },
  {
    q: "¿Cómo pueden participar los hombres y los niños?",
    a: (
      <>
        <p>
          Invitamos a niños, hombres y personas de diferentes identidades de género a
          unirse para trabajar juntos y lograr la igualdad de género. Cualquiera que tenga
          entre 13 y 24 años es bienvenido a unirse a nuestro programa de Clubs y defender a
          las niñas del mundo.
        </p>
        <p>
          Consulta esta guía antes de integrar a un hombre como miembro activo del Club:{" "}
          <Ext href="https://www.canva.com/design/DAGdJwxaHd8/R4kBf81CPlJSttofS9DYWQ/edit?utm_content=DAGdJwxaHd8&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton">
            Guía en Canva
          </Ext>
        </p>
      </>
    ),
  },
  {
    q: "¿Qué reportes debe entregar mi Club?",
    a: (
      <>
        <p>Existen 3 tipos de reportes:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <span className="font-semibold text-brand-ink">Reporte de Inicio</span>{" "}
            (Kick-Off Strategy): se llena una sola vez al inicio del año, en enero. Deben
            establecer:
            <ul className="list-[circle] pl-5 mt-1.5 space-y-1">
              <li>
                <span className="font-semibold text-brand-ink">Metas:</span> 3 metas ni muy
                específicas ni muy generales (ej. aumentar 20% el número de colaboradorxs).
              </li>
              <li>
                <span className="font-semibold text-brand-ink">Consejo de liderazgo:</span>{" "}
                definir los roles del equipo de trabajo.
              </li>
              <li>
                <span className="font-semibold text-brand-ink">Miembrxs:</span> cantidad de
                colaboradxs hasta ese momento.
              </li>
            </ul>
          </li>
          <li>
            <span className="font-semibold text-brand-ink">Reportes de progreso</span>{" "}
            (Progress Report): dos veces, en el intermedio y al final del año de gestión,
            como continuación del Reporte de Inicio.
          </li>
          <li>
            <span className="font-semibold text-brand-ink">Reporte de Actividad:</span> se
            llena cada vez que finalicen una actividad. ¡Queremos saber las cosas increíbles
            que están haciendo!{" "}
            <Ext href="https://bit.ly/ReportesClubJL">Reportes del Club</Ext>
          </li>
        </ul>
      </>
    ),
  },
  {
    q: "¿Podemos acompañar casos de aborto?",
    a: (
      <p>
        No. Juventudes Latentes y sus Clubs solamente acercan información sobre derechos
        reproductivos y canalizan con expertas en sexualidad y acompañantes de aborto. Desde
        2010, las chicas han recaudado más de 10 millones de dólares para programas que
        aseguran que miles de niñas alrededor del mundo estén a salvo, saludables y con
        acceso a la educación.
      </p>
    ),
  },
];

const Faqs = () => {
  const [faqs, setFaqs] = useState([]);
  useEffect(() => {
    api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="subpage-faqs">
      {/* Hero degradado naranja/rosa */}
      <section
        className="relative overflow-hidden border-b border-[#E5E5E5]"
        style={{
          background: `linear-gradient(120deg, ${COLORS.orange} 0%, ${COLORS.magenta} 100%)`,
        }}
      >
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Link
            to="/recursos"
            data-testid="subpage-back"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white mb-6"
          >
            <ArrowLeft size={16} /> Recursos
          </Link>
          <span className="inline-block w-16 h-1.5 rounded-sm mb-6 bg-white" />
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[0.98]">
            Preguntas Frecuentes
          </h1>
          <p className="mt-5 text-lg md:text-xl text-white/90 font-medium max-w-2xl">
            Resolvemos las dudas más comunes sobre nuestro modelo, los Clubs, donaciones y
            colaboración.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        {/* Aside destacado */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="flex items-start gap-3 rounded-sm p-5 mb-10"
          style={{ background: "#FFF3E0", border: `1px solid ${COLORS.orange}` }}
        >
          <Sparkles size={22} className="text-brand-orange flex-shrink-0 mt-0.5" />
          <p className="text-brand-ink/85">
            <span className="font-semibold text-brand-ink">Juntas somos más fuertes:</span>{" "}
            aquí encontrarás respuestas a preguntas frecuentes para ayudarte a tener una
            mejor experiencia en tu Club.
          </p>
        </motion.div>

        <Accordion type="single" collapsible className="w-full" data-testid="faqs-accordion">
          {faqs.map((item, i) => (
            <AccordionItem
              key={item.id || i}
              value={`faq-${i}`}
              data-testid={`faq-item-${i}`}
              className="border-b border-[#E5E5E5]"
            >
              <AccordionTrigger className="font-heading text-base md:text-lg font-semibold tracking-tight text-brand-ink hover:no-underline py-5 gap-4">
                {item.question}
              </AccordionTrigger>
              <AccordionContent className="text-brand-ink/80 leading-relaxed space-y-3 text-base pr-2 pb-6">
                {item.answer.split("\n").filter(Boolean).map((p, j) => (
                  <p key={j}>{p}</p>
                ))}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Contacto */}
        <div className="mt-12 bg-white border border-[#E5E5E5] rounded-sm p-6 flex items-start gap-3">
          <Mail size={20} className="text-brand-magenta flex-shrink-0 mt-0.5" />
          <p className="text-brand-ink/80">
            ¿No encontraste tu respuesta? Escríbenos a{" "}
            <MailLink email="comunidad@latentes.mx" /> y con gusto te ayudamos.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Faqs;
